import { useState } from 'react';

interface Tenant {
  id: string;
  name: string;
  plan: 'Free' | 'Pro' | 'Enterprise';
  users: number;
  createdAt: Date;
  status: 'active' | 'inactive';
  totalMessages?: number;
  domain?: string;
  contactEmail?: string;
  phone?: string;
  address?: string;
  industry?: string;
  website?: string;
}

interface SystemUser {
  id: string;
  name: string;
  email: string;
  tenantId: string;
  role: 'user' | 'admin';
  status: 'active' | 'inactive';
  lastLogin: Date;
  totalMessages?: number;
}

interface AIModel {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  availableForPlan: boolean;
  messageCount?: number;
}

interface AIProvider {
  id: string;
  name: string;
  enabled: boolean;
  availableForPlan: boolean;
  models: AIModel[];
  totalMessages?: number;
}

interface TenantDetailsProps {
  tenant: Tenant;
  users: SystemUser[];
  onClose: () => void;
  onUpdateTenant?: (tenant: Tenant) => void;
  onUpdateUserStatus?: (userId: string, status: 'active' | 'inactive') => void;
  onUpdateAIConfig?: (tenantId: string, aiId: string, modelId: string, enabled: boolean) => void;
}

export function TenantDetails({ 
  tenant, 
  users, 
  onClose, 
  onUpdateTenant,
  onUpdateUserStatus,
  onUpdateAIConfig 
}: TenantDetailsProps) {
  const [activeTab, setActiveTab] = useState<'edit' | 'metrics' | 'users' | 'ai-config'>('edit');
  const [editedTenant, setEditedTenant] = useState<Tenant>({
    ...tenant,
    domain: tenant.domain || 'alpha.com.br',
    contactEmail: tenant.contactEmail || 'contato@alpha.com.br',
    phone: tenant.phone || '+55 11 99999-9999',
    address: tenant.address || 'São Paulo, SP',
    industry: tenant.industry || 'Tecnologia',
    website: tenant.website || 'https://alpha.com.br'
  });

  const [tenantAIs, setTenantAIs] = useState<AIProvider[]>([
    {
      id: 'claude',
      name: 'Claude (Anthropic)',
      enabled: tenant.plan === 'Pro' || tenant.plan === 'Enterprise',
      availableForPlan: tenant.plan === 'Pro' || tenant.plan === 'Enterprise',
      totalMessages: 1250,
      models: [
        {
          id: 'claude-3-opus-20240229',
          name: 'Claude 3 Opus',
          description: 'Modelo mais avançado da Anthropic',
          enabled: tenant.plan === 'Enterprise',
          availableForPlan: tenant.plan === 'Enterprise',
          messageCount: 450
        },
        {
          id: 'claude-3-sonnet',
          name: 'Claude 3 Sonnet',
          description: 'Equilibrio entre performance e velocidade',
          enabled: tenant.plan === 'Pro' || tenant.plan === 'Enterprise',
          availableForPlan: tenant.plan === 'Pro' || tenant.plan === 'Enterprise',
          messageCount: 650
        },
        {
          id: 'claude-3-haiku',
          name: 'Claude 3 Haiku',
          description: 'Rápido e eficiente',
          enabled: tenant.plan === 'Pro' || tenant.plan === 'Enterprise',
          availableForPlan: tenant.plan === 'Pro' || tenant.plan === 'Enterprise',
          messageCount: 150
        }
      ]
    },
    {
      id: 'gpt',
      name: 'GPT (OpenAI)',
      enabled: true,
      availableForPlan: true,
      totalMessages: 2100,
      models: [
        {
          id: 'gpt-5',
          name: 'GPT-5',
          description: 'Modelo mais avançado da OpenAI - Nova geração',
          enabled: tenant.plan === 'Enterprise',
          availableForPlan: tenant.plan === 'Enterprise',
          messageCount: 500
        },
        {
          id: 'gpt-4',
          name: 'GPT-4',
          description: 'Modelo mais avançado da OpenAI',
          enabled: tenant.plan === 'Pro' || tenant.plan === 'Enterprise',
          availableForPlan: tenant.plan === 'Pro' || tenant.plan === 'Enterprise',
          messageCount: 1200
        },
        {
          id: 'gpt-3.5-turbo',
          name: 'GPT-3.5 Turbo',
          description: 'Rápido e eficiente',
          enabled: true,
          availableForPlan: true,
          messageCount: 900
        }
      ]
    },
    {
      id: 'gemini',
      name: 'Gemini (Google)',
      enabled: tenant.plan === 'Pro' || tenant.plan === 'Enterprise',
      availableForPlan: tenant.plan === 'Pro' || tenant.plan === 'Enterprise',
      totalMessages: 850,
      models: [
        {
          id: 'gemini-pro',
          name: 'Gemini Pro',
          description: 'Modelo mais capaz do Google',
          enabled: tenant.plan === 'Pro' || tenant.plan === 'Enterprise',
          availableForPlan: tenant.plan === 'Pro' || tenant.plan === 'Enterprise',
          messageCount: 500
        },
        {
          id: 'gemini-pro-vision',
          name: 'Gemini Pro Vision',
          description: 'Capacidades multimodais',
          enabled: tenant.plan === 'Enterprise',
          availableForPlan: tenant.plan === 'Enterprise',
          messageCount: 350
        }
      ]
    },
    {
      id: 'perplexity',
      name: 'Perplexity',
      enabled: tenant.plan === 'Enterprise',
      availableForPlan: tenant.plan === 'Enterprise',
      totalMessages: 320,
      models: [
        {
          id: 'perplexity-pro',
          name: 'Perplexity Pro',
          description: 'IA focada em pesquisa',
          enabled: tenant.plan === 'Enterprise',
          availableForPlan: tenant.plan === 'Enterprise',
          messageCount: 200
        },
        {
          id: 'perplexity-fast',
          name: 'Perplexity Fast',
          description: 'Respostas rápidas',
          enabled: tenant.plan === 'Enterprise',
          availableForPlan: tenant.plan === 'Enterprise',
          messageCount: 120
        }
      ]
    }
  ]);

  const tenantUsers = users.filter(user => user.tenantId === tenant.id);
  const totalMessages = tenantAIs.reduce((sum, ai) => sum + (ai.totalMessages || 0), 0);

  const handleModelToggle = (aiId: string, modelId: string) => {
    const ai = tenantAIs.find(a => a.id === aiId);
    const model = ai?.models.find(m => m.id === modelId);
    
    if (!model?.availableForPlan) return;

    setTenantAIs(prev => 
      prev.map(ai => 
        ai.id === aiId 
          ? {
              ...ai,
              models: ai.models.map(model =>
                model.id === modelId
                  ? { ...model, enabled: !model.enabled }
                  : model
              )
            }
          : ai
      )
    );

    if (onUpdateAIConfig) {
      onUpdateAIConfig(tenant.id, aiId, modelId, !model.enabled);
    }
  };

  const handleProviderToggle = (aiId: string) => {
    const ai = tenantAIs.find(a => a.id === aiId);
    if (!ai?.availableForPlan) return;

    const newEnabled = !ai.enabled;
    
    setTenantAIs(prev => 
      prev.map(provider => 
        provider.id === aiId 
          ? {
              ...provider,
              enabled: newEnabled,
              models: provider.models.map(model => ({
                ...model,
                enabled: newEnabled && model.availableForPlan
              }))
            }
          : provider
      )
    );
  };

  const handleSaveTenant = () => {
    if (onUpdateTenant) {
      onUpdateTenant(editedTenant);
    }
  };

  const handleStatusToggle = () => {
    const newStatus = editedTenant.status === 'active' ? 'inactive' : 'active';
    setEditedTenant(prev => ({ ...prev, status: newStatus }));
  };

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'Enterprise':
        return 'bg-purple-100 text-purple-800';
      case 'Pro':
        return 'bg-blue-100 text-blue-800';
      case 'Free':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    return status === 'active' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const tabs = [
    { id: 'edit', label: 'Dados da Empresa', icon: '🏢' },
    { id: 'metrics', label: 'Métricas de Uso', icon: '📊' },
    { id: 'users', label: 'Usuários', icon: '👥' },
    { id: 'ai-config', label: 'Configuração IA', icon: '🤖' }
  ];

  const getUsageMetrics = () => {
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
    const growth = Math.floor(Math.random() * 30) + 5; // Simulated growth

    return {
      monthlyMessages: totalMessages,
      weeklyMessages: Math.floor(totalMessages * 0.25),
      dailyAverage: Math.floor(totalMessages / 30),
      growthRate: growth,
      activeUsers: tenantUsers.filter(u => u.status === 'active').length,
      peakHour: '14:00 - 15:00',
      mostUsedAI: tenantAIs.reduce((prev, current) => 
        (prev.totalMessages || 0) > (current.totalMessages || 0) ? prev : current
      ).name
    };
  };

  const metrics = getUsageMetrics();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-blue-600 text-lg">🏢</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{editedTenant.name}</h2>
              <div className="flex items-center space-x-2 mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanBadgeColor(editedTenant.plan)}`}>
                  {editedTenant.plan}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(editedTenant.status)}`}>
                  {editedTenant.status === 'active' ? 'Ativo' : 'Inativo'}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  {totalMessages.toLocaleString()} mensagens
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleStatusToggle}
              className={`px-3 py-1 text-sm rounded-md ${
                editedTenant.status === 'active'
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {editedTenant.status === 'active' ? 'Desativar' : 'Ativar'}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[600px]">
          {activeTab === 'edit' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Informações da Empresa</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome da Empresa *
                  </label>
                  <input
                    type="text"
                    value={editedTenant.name}
                    onChange={(e) => setEditedTenant(prev => ({ ...prev, name: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plano *
                  </label>
                  <select
                    value={editedTenant.plan}
                    onChange={(e) => setEditedTenant(prev => ({ ...prev, plan: e.target.value as any }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Free">Free</option>
                    <option value="Pro">Pro</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Domínio
                  </label>
                  <input
                    type="text"
                    value={editedTenant.domain}
                    onChange={(e) => setEditedTenant(prev => ({ ...prev, domain: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="empresa.com.br"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email de Contato
                  </label>
                  <input
                    type="email"
                    value={editedTenant.contactEmail}
                    onChange={(e) => setEditedTenant(prev => ({ ...prev, contactEmail: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="contato@empresa.com.br"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    value={editedTenant.phone}
                    onChange={(e) => setEditedTenant(prev => ({ ...prev, phone: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+55 11 99999-9999"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Setor/Indústria
                  </label>
                  <select
                    value={editedTenant.industry}
                    onChange={(e) => setEditedTenant(prev => ({ ...prev, industry: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Tecnologia">Tecnologia</option>
                    <option value="Saúde">Saúde</option>
                    <option value="Educação">Educação</option>
                    <option value="Financeiro">Financeiro</option>
                    <option value="E-commerce">E-commerce</option>
                    <option value="Consultoria">Consultoria</option>
                    <option value="Manufatura">Manufatura</option>
                    <option value="Outros">Outros</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    value={editedTenant.website}
                    onChange={(e) => setEditedTenant(prev => ({ ...prev, website: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://empresa.com.br"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={editedTenant.status}
                    onChange={(e) => setEditedTenant(prev => ({ ...prev, status: e.target.value as any }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Endereço
                </label>
                <textarea
                  value={editedTenant.address}
                  onChange={(e) => setEditedTenant(prev => ({ ...prev, address: e.target.value }))}
                  rows={3}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Endereço completo da empresa"
                />
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Informações do Sistema</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-blue-700">ID do Tenant:</span>
                    <span className="ml-2 text-blue-600">{editedTenant.id}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-700">Data de Criação:</span>
                    <span className="ml-2 text-blue-600">{editedTenant.createdAt.toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-700">Total de Usuários:</span>
                    <span className="ml-2 text-blue-600">{tenantUsers.length}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-700">Total de Mensagens:</span>
                    <span className="ml-2 text-blue-600">{totalMessages.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'metrics' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Métricas de Uso</h3>
              
              {/* Cards de Métricas Principais */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm font-medium text-blue-600">Mensagens este Mês</div>
                  <div className="text-2xl font-bold text-blue-900">{metrics.monthlyMessages.toLocaleString()}</div>
                  <div className="text-xs text-blue-600 mt-1">+{metrics.growthRate}% vs mês anterior</div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm font-medium text-green-600">Média Diária</div>
                  <div className="text-2xl font-bold text-green-900">{metrics.dailyAverage}</div>
                  <div className="text-xs text-green-600 mt-1">mensagens/dia</div>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-sm font-medium text-purple-600">Usuários Ativos</div>
                  <div className="text-2xl font-bold text-purple-900">{metrics.activeUsers}</div>
                  <div className="text-xs text-purple-600 mt-1">de {tenantUsers.length} total</div>
                </div>
                
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="text-sm font-medium text-orange-600">IA Mais Usada</div>
                  <div className="text-lg font-bold text-orange-900">{metrics.mostUsedAI}</div>
                  <div className="text-xs text-orange-600 mt-1">maior volume</div>
                </div>
              </div>

              {/* Gráfico de Uso por IA */}
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Uso por Inteligência Artificial</h4>
                <div className="space-y-4">
                  {tenantAIs.filter(ai => ai.enabled).map(ai => {
                    const percentage = ((ai.totalMessages || 0) / totalMessages) * 100;
                    return (
                      <div key={ai.id}>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">{ai.name}</span>
                          <span className="text-sm text-gray-500">{(ai.totalMessages || 0).toLocaleString()} mensagens</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{percentage.toFixed(1)}% do total</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Métricas Detalhadas */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Detalhes de Uso</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Horário de Pico:</span>
                      <span className="text-sm font-medium text-gray-900">{metrics.peakHour}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Mensagens esta Semana:</span>
                      <span className="text-sm font-medium text-gray-900">{metrics.weeklyMessages.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Média por Usuário Ativo:</span>
                      <span className="text-sm font-medium text-gray-900">{Math.round(totalMessages / metrics.activeUsers)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">IAs Habilitadas:</span>
                      <span className="text-sm font-medium text-gray-900">{tenantAIs.filter(ai => ai.enabled).length} de {tenantAIs.length}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Top Usuários</h4>
                  <div className="space-y-3">
                    {tenantUsers
                      .sort((a, b) => (b.totalMessages || 0) - (a.totalMessages || 0))
                      .slice(0, 5)
                      .map(user => (
                        <div key={user.id} className="flex justify-between items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-xs text-gray-500">{user.email}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-900">
                              {(user.totalMessages || Math.floor(Math.random() * 500) + 50).toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">mensagens</div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Usuários do Tenant</h3>
                <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">
                  Adicionar Usuário
                </button>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuário</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mensagens</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Último Login</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tenantUsers.map(user => (
                      <tr key={user.id}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.role === 'admin' ? 'Admin' : 'Usuário'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(user.status)}`}>
                            {user.status === 'active' ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {(user.totalMessages || Math.floor(Math.random() * 500) + 50).toLocaleString()}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.lastLogin.toLocaleDateString('pt-BR')}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                          <button 
                            onClick={() => onUpdateUserStatus && onUpdateUserStatus(user.id, user.status === 'active' ? 'inactive' : 'active')}
                            className={`text-sm ${
                              user.status === 'active' 
                                ? 'text-red-600 hover:text-red-900' 
                                : 'text-green-600 hover:text-green-900'
                            }`}
                          >
                            {user.status === 'active' ? 'Desativar' : 'Ativar'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {tenantUsers.length === 0 && (
                  <div className="p-6 text-center">
                    <div className="text-gray-500">Nenhum usuário encontrado para este tenant.</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'ai-config' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Configuração Detalhada de IAs</h3>
              <p className="text-sm text-gray-600">
                Configure quais inteligências artificiais e modelos específicos estarão disponíveis para os usuários deste tenant.
                As opções disponíveis dependem do plano contratado.
              </p>
              
              <div className="space-y-6">
                {tenantAIs.map(ai => (
                  <div key={ai.id} className="bg-white border border-gray-200 rounded-lg">
                    {/* AI Provider Header */}
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            ai.enabled ? 'bg-green-100' : 'bg-gray-100'
                          }`}>
                            <span className="text-lg">🤖</span>
                          </div>
                          <div>
                            <h4 className="text-lg font-medium text-gray-900">{ai.name}</h4>
                            <p className="text-sm text-gray-500">
                              {ai.availableForPlan ? 'Disponível no seu plano' : 'Não disponível no seu plano'} • 
                              {(ai.totalMessages || 0).toLocaleString()} mensagens
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <span className={`text-sm font-medium ${
                            ai.enabled ? 'text-green-600' : 'text-gray-500'
                          }`}>
                            {ai.enabled ? 'Habilitado' : 'Desabilitado'}
                          </span>
                          
                          <button
                            onClick={() => handleProviderToggle(ai.id)}
                            disabled={!ai.availableForPlan}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              ai.availableForPlan
                                ? ai.enabled
                                  ? 'bg-green-600'
                                  : 'bg-gray-200'
                                : 'bg-gray-100 cursor-not-allowed'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                ai.enabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Models List */}
                    <div className="p-4">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Modelos Disponíveis</h5>
                      <div className="space-y-3">
                        {ai.models.map(model => (
                          <div key={model.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h6 className="text-sm font-medium text-gray-900">{model.name}</h6>
                                  <p className="text-xs text-gray-500">{model.description}</p>
                                </div>
                                <div className="text-right mr-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {(model.messageCount || 0).toLocaleString()}
                                  </div>
                                  <div className="text-xs text-gray-500">mensagens</div>
                                </div>
                              </div>
                              
                              {!model.availableForPlan && (
                                <div className="mt-2 text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
                                  Requer plano superior
                                </div>
                              )}
                            </div>
                            
                            <button
                              onClick={() => handleModelToggle(ai.id, model.id)}
                              disabled={!model.availableForPlan}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ml-4 ${
                                model.availableForPlan
                                  ? model.enabled
                                    ? 'bg-green-600'
                                    : 'bg-gray-200'
                                  : 'bg-gray-100 cursor-not-allowed'
                              }`}
                            >
                              <span
                                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                  model.enabled ? 'translate-x-5' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {!ai.availableForPlan && (
                      <div className="p-4 bg-yellow-50 border-t border-yellow-200">
                        <p className="text-sm text-yellow-800">
                          Esta IA não está disponível no plano <strong>{editedTenant.plan}</strong>. 
                          Faça upgrade do plano para habilitar esta funcionalidade.
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Limitações por Plano</h4>
                <div className="text-xs text-blue-800 space-y-1">
                  <div><strong>Free:</strong> Apenas GPT-3.5 Turbo</div>
                  <div><strong>Pro:</strong> GPT-4, Claude Sonnet/Haiku, Gemini Pro</div>
                  <div><strong>Enterprise:</strong> Todos os modelos disponíveis incluindo Opus e Vision</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Fechar
          </button>
          <div className="space-x-3">
            <button
              onClick={handleSaveTenant}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              Salvar Alterações
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}